import type { FunctionalComponent } from 'vue'

export interface ILink {
  text: string
  url: string
  target?: string
  rel?: string
}
export interface ISidebar {
  text: string
  url: string
  icon: FunctionalComponent
  news?: number
  adminOnly?: boolean
  requiresProject?: boolean
}

interface INameValue<T> {
  name: string
  value: T
}

export class NameValue<T> implements INameValue<T> {
  constructor(
    public name: string = '',
    public value: T
  ) {}
}

export enum UserRole {
  ADMINISTRATOR,
  WRITE,
  READ,
  NONE
}

export interface IUser {
  oid: string
  name: string
  role: UserRole
  username: string
  projects?: (string | number)[]
  canManageUsers?: boolean
  managedUsernames?: string[]
  managedProjectIds?: number[]
  impersonated?: boolean
  impersonatorUsername?: string | null
  impersonatedRole?: UserRole | null
  impersonatedProjectIds?: number[] | null
  impersonationMode?: 'user' | 'access' | null

  isAdmin: () => boolean
  isWriter: () => boolean
  isReader: () => boolean
  isAuthenticated: () => boolean
  isImpersonating: () => boolean
}

export class User implements IUser {
  constructor(
    public oid: string = '',
    public name: string = '',
    public role: UserRole = UserRole.NONE,
    public username: string = '',
    public projects: (string | number)[] = [],
    public canManageUsers: boolean = false,
    public managedUsernames: string[] = [],
    public managedProjectIds: number[] = [],
    public impersonated: boolean = false,
    public impersonatorUsername: string | null = null,
    public impersonatedRole: UserRole | null = null,
    public impersonatedProjectIds: number[] | null = null,
    public impersonationMode: 'user' | 'access' | null = null
  ) {}

  isAdmin = () => this.role === UserRole.ADMINISTRATOR
  isWriter = () => this.role === UserRole.WRITE
  isReader = () => this.role === UserRole.READ
  isAuthenticated = () => this.role !== UserRole.NONE
  isImpersonating = () => this.impersonated
}

interface IBase {
  id: number
  timestamp: Date
  created: Date
}

export interface IFile extends IBase {
  uuid: string
  name: string
  mlModelId: number | null
  projectId: number
}

export class File implements IFile {
  constructor(
    public id: number,
    public timestamp: Date,
    public created: Date,
    public uuid: string,
    public name: string,
    public mlModelId: number | null,
    public projectId: number
  ) {}
}

export interface IAnnotation extends IBase {
  kind: 'prediction' | 'feedback'
  resolved: boolean
  label: string | null
  score: number | null
  trimStart: number | null
  trimEnd: number | null
  comment: string | null
  trustLevel: number | null
  file: File
}

export class Annotation implements IAnnotation {
  constructor(
    public id: number,
    public timestamp: Date,
    public created: Date,
    public kind: 'prediction' | 'feedback',
    public resolved: boolean,
    public label: string | null,
    public score: number | null,
    public trimStart: number | null,
    public trimEnd: number | null,
    public comment: string | null,
    public trustLevel: number | null,
    public file: File
  ) {}
}

export type Feedback = Annotation // Backwards compatibility alias

export interface ITaxonomySuggestion extends IBase {
  resolved: boolean
  name: string
  projectId: number
}

export class TaxonomySuggestion implements ITaxonomySuggestion {
  constructor(
    public id: number,
    public timestamp: Date,
    public created: Date,
    public resolved: boolean,
    public name: string,
    public projectId: number
  ) {}
}

interface IBaseStatistics {
  date: string
  count: number
  cumulative_count: number
}

export interface IProjectUsageStat {
  project_name: string
  project_origin?: string
  files?: number
  feedback?: number
  annotations?: number
  last_activity?: string
}

export interface IStatistics {
  file: IBaseStatistics[]
  feedback: IBaseStatistics[]
  annotations: INameValue<number>[]
  annotations_all_time: INameValue<number>[]
  unresolved_annotations_count: number
  resolved_annotations_count: number
  project_usage?: IProjectUsageStat[]
}
export class Statistics implements IStatistics {
  constructor(
    public file: IBaseStatistics[],
    public feedback: IBaseStatistics[],
    public annotations: INameValue<number>[],
    public annotations_all_time: INameValue<number>[],
    public unresolved_annotations_count: number,
    public resolved_annotations_count: number,
    public project_usage: IProjectUsageStat[] = []
  ) {}
}

export interface IML_Model extends IBase {
  name: string
  projectId: number
}

export class ML_Model implements IML_Model {
  constructor(
    public id: number,
    public timestamp: Date,
    public created: Date,
    public name: string,
    public projectId: number
  ) {}
}

// Plotly

import type { Data, Datum, Layout, PlotData, Shape } from 'plotly.js-dist-min'

export type PlotlyData<
  X extends Datum = number,
  Y extends Datum | Datum[] = number
> = Partial<PlotData> & {
  x: X[]
  y: Y[]
}

export type PlotlyDataDx = Omit<Partial<PlotData>, 'x'> & {
  x0: Date
  dx: number
  y: Datum[] | Datum[][]
}

export type PlotlySetup = {
  queryKeys: string[]
  title: string
  yRange: [number, number]
  layout: PlotlyLayout
  plotlyData?: PlotlyGenericData[]
  triggers?: number[]
  periodLength?: number
}

export type PlotlyShape = Partial<Shape>

export type PlotlyLayout = Partial<Layout>

export type PlotlyGenericData = Data & { surfacecolor?: number[][] }
